/**
 * status.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * System Status — Route: /status
 * Placeholder uptime + incident data. Wire `SERVICES`/`INCIDENTS` up to a real
 * status source (Supabase table your ops team updates, or a status-page
 * provider's API) later.
 */
import { useState, useEffect } from "react";
import {
  ArrowLeft, CheckCircle2, AlertTriangle, XCircle, Activity, Clock,
} from "lucide-react";
import { BASE_CSS, Sidebar, Topbar, DEFAULT_SETTINGS, buildThemeVars, applyThemeVars, useCurrentUser } from "../shared/AppShell";

const PAGE_CSS = `
  .st-banner {
    display:flex; align-items:center; gap:12px; padding:16px 20px;
    background:var(--green-dim); border:1px solid rgba(52,211,153,.25);
    border-radius:var(--r-lg); margin-bottom:24px;
  }
  .st-banner.degraded { background:var(--amber-dim); border-color:rgba(245,158,11,.25); }
  .st-banner-icon { color:var(--green); flex-shrink:0; }
  .st-banner.degraded .st-banner-icon { color:var(--amber); }
  .st-banner-title { font-size:14px; font-weight:700; }
  .st-banner-sub { font-size:11.5px; color:var(--muted); margin-top:2px; }

  .st-service { display:flex; align-items:center; gap:14px; padding:15px 20px; border-bottom:1px solid var(--border); }
  .st-service:last-child { border-bottom:none; }
  .st-service-name { font-size:13px; font-weight:600; flex:1; }
  .st-bars { display:flex; gap:2px; }
  .st-bar { width:5px; height:20px; border-radius:2px; background:var(--green); }
  .st-bar.degraded { background:var(--amber); }
  .st-bar.down { background:var(--red); }
  .st-bar.empty { background:var(--border2); }
  .st-uptime { font-family:var(--mono); font-size:12px; color:var(--muted); width:64px; text-align:right; }

  .st-incident { padding:16px 20px; border-bottom:1px solid var(--border); }
  .st-incident:last-child { border-bottom:none; }
  .st-incident-head { display:flex; align-items:center; gap:10px; margin-bottom:6px; }
  .st-incident-title { font-size:13px; font-weight:700; }
  .st-incident-time { font-size:11px; color:var(--faint); margin-left:auto; }
  .st-incident-desc { font-size:12px; color:var(--muted); line-height:1.6; }
`;

const SERVICES = [
  { name: "Trading Terminal & Live Prices", uptime: "99.98%", bars: Array(30).fill("ok") },
  { name: "Wallets & Deposits", uptime: "99.99%", bars: Array(30).fill("ok") },
  { name: "Copy Trading Engine", uptime: "99.95%", bars: [...Array(27).fill("ok"), "degraded", "ok", "ok"] },
  { name: "Marketplace & Subscriptions", uptime: "100%", bars: Array(30).fill("ok") },
  { name: "Notifications", uptime: "99.91%", bars: [...Array(25).fill("ok"), "down", "ok", "ok", "ok", "ok"] },
  { name: "Support & Ticketing", uptime: "100%", bars: Array(30).fill("ok") },
];

const INCIDENTS = [
  { title: "Notifications delayed for some users", time: "3 days ago", status: "resolved", desc: "Push notifications were delayed by up to 20 minutes due to a queue backlog. Resolved after scaling the notification worker." },
  { title: "Copy trading sync lag on EUR pairs", time: "6 days ago", status: "resolved", desc: "A small subset of copy-trading accounts experienced a 30–60 second sync delay on EUR pairs during high volatility. Fixed by increasing broker connection throughput." },
];

function statusMeta(status) {
  if (status === "resolved") return { Icon: CheckCircle2, color: "var(--green)" };
  if (status === "monitoring") return { Icon: AlertTriangle, color: "var(--amber)" };
  return { Icon: XCircle, color: "var(--red)" };
}

export default function StatusPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const user = useCurrentUser();

  useEffect(() => { applyThemeVars(buildThemeVars(DEFAULT_SETTINGS)); }, []);

  const allOperational = SERVICES.every((s) => s.bars.every((b) => b === "ok"));

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: BASE_CSS + PAGE_CSS }} />
      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 299 }} />}
      <div className="in-shell">
        <Sidebar open={sidebarOpen} active="/support" />
        <div className="in-right">
          <Topbar onMenu={() => setSidebarOpen((v) => !v)} user={user} title="System" accent="Status" />
          <main className="in-main">
            <div className="pg-back" onClick={() => (window.location.href = "/support")}>
              <ArrowLeft size={13} /> Back to Help Center
            </div>

            <div className="pg-hero">
              <div className="pg-hero-grid" />
              <div className="pg-hero-content">
                <div className="pg-hero-label">Status</div>
                <div className="pg-hero-title">Platform <em>uptime</em></div>
                <div className="pg-hero-sub">Live status of every core service, plus a history of past incidents.</div>
              </div>
            </div>

            <div className={`st-banner${allOperational ? "" : " degraded"}`}>
              <Activity size={20} className="st-banner-icon" />
              <div>
                <div className="st-banner-title">{allOperational ? "All systems operational" : "Minor issues detected"}</div>
                <div className="st-banner-sub">Last checked just now · Updates automatically every few minutes</div>
              </div>
            </div>

            <div className="pg-card" style={{ marginBottom: 24 }}>
              <div className="pg-card-head"><div className="pg-card-title"><Activity size={16} /> Services (last 30 days)</div></div>
              <div>
                {SERVICES.map((s, i) => (
                  <div key={i} className="st-service">
                    <div className="st-service-name">{s.name}</div>
                    <div className="st-bars">
                      {s.bars.map((b, j) => <div key={j} className={`st-bar ${b}`} />)}
                    </div>
                    <div className="st-uptime">{s.uptime}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pg-card">
              <div className="pg-card-head"><div className="pg-card-title"><Clock size={16} /> Past incidents</div></div>
              <div>
                {INCIDENTS.map((inc, i) => {
                  const { Icon, color } = statusMeta(inc.status);
                  return (
                    <div key={i} className="st-incident">
                      <div className="st-incident-head">
                        <Icon size={15} style={{ color }} />
                        <div className="st-incident-title">{inc.title}</div>
                        <div className="st-incident-time">{inc.time}</div>
                      </div>
                      <div className="st-incident-desc">{inc.desc}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}